import SectionWrapper from '@/components/_rebrand/craft/SectionWrapper';
import { StyleOverride } from '@/types/craft-style-overrides';
import cx from 'classnames';
import { FC } from 'react';

export interface ThreeColumnMediaFullProps {
  title: React.ReactNode,
  features: {
    icon: React.ReactNode,
    title: string;
    description: string | JSX.Element;
  }[]
  mainImage?: React.ReactNode;
  styleOverrides?: StyleOverride;
}

/**
 * The ThreeColumnMediaFull component is a layout that displays a title, a grid of features, and an optional main image.
 * In Craft, it's called "3x Benefits". 
 * On some Craft pages, a "style override" is applied to the component which has several effects, including:
 * - Making the title wider, and with a smaller font size. 
 * - Removing a lot of the upper padding. 
 */
const ThreeColumnMediaFull: FC<ThreeColumnMediaFullProps> = ({
  title,
  features = [],
  mainImage,
  styleOverrides
}) => {

  const pageSpecificHeaderClasses: string = styleOverrides ? 'md:text-36/42 max-w-[1024px]' : 'md:text-43/53 max-w-[700px]';

  return (
    <div className="bg-white text-white">
      <SectionWrapper styleOverrides={styleOverrides}>
        <div className={cx(!styleOverrides ? 'pt-header-height-mobile md:pt-header-height-desktop' : 'pt-1')}>
          <div className={cx('pt-0', !styleOverrides ? 'md:pt-12' : 'pt-0', 'px-container-mobile md:px-container-desktop mx-auto max-w-grid')}>
            <div className="grid gap-2 md:gap-3 text-left">
              <div className="flex justify-center">
                <h1
                  className={cx('text-blue-900 text-25/33', pageSpecificHeaderClasses, 'font-500 text-left md:text-center mb-[48px] md:mb-[64px]')}>{title}</h1>
              </div>
              <div className="flex justify-between flex-wrap md:flex-nowrap">
                {features.map((item, index) => {
                  return (
                    <div key={`feature-card-${index}`}
                      className="flex flex-wrap w-full md:max-w-[344px] mb-[32px] mr-[16px] md:mb-0">
                      <div className="flex">
                        <div className="flex" id="icon">
                          <span
                            className="flex w-[32px] h-[32px] bg-citron-500 rounded-[50%] justify-center items-center mr-[16px]">
                            {item.icon}
                          </span>
                        </div>
                        <div className="flex text-21/31 text-blue-900 font-500" id="textHeader">
                          {item.title}
                        </div>
                      </div>
                      <div className="flex text-17/27 text-gray-700 mt-[18px]">
                        {item.description}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            {mainImage ? (
              <div className="flex justify-center w-auto md:w-full pt-0 md:py-[76px] mt-[64px] rounded-10 overflow-hidden">
                <div className="rounded-10 overflow-hidden w-full">
                  {mainImage}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
};

export default ThreeColumnMediaFull;
